import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  Leaf,
  Brain,
  FlaskConical,
  Stethoscope,
  ShieldCheck,
  ExternalLink,
  Lock,
  Globe,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const PILLARS = [
  {
    n: "01",
    icon: Brain,
    title: "Health Intelligence",
    text: "Live metabolic dashboard — biological age, visceral index, weekly trends, and AI insight engine.",
    href: "https://nutramilo.com/dashboard/health-intelligence",
    cta: "Open dashboard",
  },
  {
    n: "02",
    icon: Leaf,
    title: "Nutrition Intelligence",
    text: "Snap-a-plate AI food recognition, insulinogenic index, USDA-grade nutrient resolution.",
    href: "https://nutramilo.com/dashboard/nutrition-lab",
    cta: "Open Nutrition Lab",
  },
  {
    n: "03",
    icon: FlaskConical,
    title: "Precision Health",
    text: "PDF Vision OCR for lab imports, AI-assisted skin / dental / breast screenings.",
    href: "https://nutramilo.com/anonymous-lab",
    cta: "Try Lab OCR",
  },
];

const TWINS = [
  ["Nutritional", "DIL · Fibers · Ω-balance"],
  ["Cognitive", "N-back · Stroop"],
  ["Laboratory", "HbA1c · LDL · hsCRP"],
  ["Phenotype", "Insulin-resistant · Balanced"],
  ["Microbiome", "30 plants/week"],
  ["Body", "BMI · BF% · Waist"],
  ["Sleep", "REM · Latency"],
  ["Stress (Cortisol)", "Meal cortisol load"],
  ["Thyroid", "TSH · FT3 · FT4"],
  ["Cardiovascular", "BP · HRV · ApoB"],
  ["Hormonal-Reproductive", "Cycle · STRAW+10"],
  ["Metabolic-Insulin", "HOMA-IR · TG/HDL"],
  ["Inflammatory", "hsCRP · NLR"],
  ["Locomotion", "Steps · VO₂ max"],
  ["Psycho-emotional", "PHQ-9 · GAD-7 · Voice"],
  ["Hepato-renal", "ALT · AST · eGFR"],
  ["Dental (DHI)", "Perio · Bruxism · Oral-systemic"],
  ["Breast (BHI)", "BDI · density · estrogen · DUTCH lab"],
];

const DEEP_LINKS = [
  {
    label: "60-second Free Assessment",
    href: "https://nutramilo.com/assessment",
    detail: "Personalized health snapshot",
  },
  {
    label: "Digital Health Twin",
    href: "https://nutramilo.com/smart/digital-twin",
    detail: "18 synced organ systems",
  },
  {
    label: "Scientific Papers",
    href: "https://nutramilo.com/science/papers",
    detail: "Peer-reviewed citation index",
  },
  {
    label: "Knowledge Library",
    href: "https://nutramilo.com/knowledge-library",
    detail: "Clinical reference articles",
  },
  {
    label: "Dental Screening",
    href: "https://nutramilo.com/dental-screening",
    detail: "AI-assisted oral health check",
  },
  {
    label: "Skin Screening",
    href: "https://nutramilo.com/skin-screening",
    detail: "Dermatological pre-assessment",
  },
];

const SOURCES = [
  "PubMed (NCBI)",
  "USDA FoodData",
  "DrugBank",
  "Semantic Scholar",
  "PubChem (NIH)",
  "ClinVar",
  "Open Targets",
  "Open Food Facts",
  "Edamam",
  "OpenAI GPT-5.2",
];

export default function NutramiloProject() {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);

  return (
    <section
      id="initiatives"
      data-testid="initiatives-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] overflow-hidden"
    >
      <AbstractShapes
        variant="rings"
        tone="ink"
        className="-right-32 top-12 w-[520px] h-[520px] opacity-70"
      />
      <AbstractShapes
        variant="wave"
        tone="ink"
        className="left-0 -bottom-16 w-[520px] h-[300px] opacity-60"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 09 / Latest Initiative · 2026
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02] max-w-3xl">
              Beyond the journal page. <br />
              <em className="not-italic text-[var(--sci-teal)]">A new chapter in open science.</em>
            </h2>
          </div>
          <p className="md:max-w-sm text-[var(--sci-text-muted)] leading-relaxed">
            This year, International Sci Ink Press launched{" "}
            <span className="text-[var(--sci-navy)] font-medium">Nutramilo</span>{" "}
            — an AI clinical-nutrition platform built on peer-reviewed evidence
            and the scientific rigor our journals demand.
          </p>
        </div>

        {/* ── HERO BLOCK: browser-frame preview + copy ─────────────────────── */}
        <div className="grid lg:grid-cols-12 gap-0 bg-white border border-[var(--sci-border)] overflow-hidden">
          {/* Left: editorial copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 p-6 sm:p-10 md:p-12 lg:p-14"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 border border-[var(--sci-navy)]/15 flex items-center justify-center text-[var(--sci-teal)]">
                <Leaf size={18} strokeWidth={1.5} />
              </div>
              <div>
                <div className="overline text-[var(--sci-text-muted)]">
                  Sci Ink Press · 2026 Project
                </div>
                <div className="font-mono-sci text-[11px] tracking-wider text-[var(--sci-navy)] mt-1">
                  NUTRAMILO · nutramilo.com
                </div>
              </div>
            </div>

            <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              Your AI Digital <br />
              <em className="not-italic text-[var(--sci-teal)]">Health Twin.</em>
            </h3>

            <p className="mt-6 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg">
              Nutramilo analyzes nutrition, labs, lifestyle, and biomarkers —
              translating complex clinical data into personalized health
              decisions across 18 connected organ-system twins.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]">
              {[
                { v: "18", l: "Digital twins" },
                { v: "37", l: "Physiological links" },
                { v: "10+", l: "Scientific DBs" },
              ].map((s) => (
                <div key={s.l} className="bg-white p-4 md:p-5">
                  <div className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] leading-none">
                    {s.v}
                  </div>
                  <div className="overline text-[var(--sci-text-muted)] mt-2">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://nutramilo.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nutramilo-card"
                className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide"
              >
                Visit Nutramilo
                <ArrowUpRight size={16} />
              </a>
              <a
                href="https://nutramilo.com/assessment"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nutramilo-assessment"
                className="btn-secondary inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide"
              >
                Free Assessment
                <Sparkles size={14} />
              </a>
            </div>

            <div className="mt-6 text-[11px] font-mono-sci tracking-wider text-[var(--sci-text-muted)] flex items-center gap-2">
              <ShieldCheck size={12} className="text-[var(--sci-teal)]" />
              NOT A MEDICAL DEVICE · MDR 2017/745
            </div>
          </motion.div>

          {/* Right: browser frame with live preview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="nutramilo-preview-frame"
            className="lg:col-span-7 relative bg-[var(--sci-navy)] min-h-[420px] lg:min-h-[640px] p-6 md:p-10 flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
            <AbstractShapes
              variant="rings"
              tone="white"
              className="inset-0 w-full h-full opacity-60"
            />

            {/* Browser chrome mockup */}
            <div className="relative w-full max-w-[680px] bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] border border-white/10">
              {/* Window bar */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--sci-border)] bg-[var(--sci-bg)]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <div className="ml-4 flex-1 flex items-center gap-2 bg-white border border-[var(--sci-border)] px-3 py-1.5 text-[11px] font-mono-sci text-[var(--sci-text-muted)] tracking-wider">
                  <Lock size={11} className="text-[var(--sci-teal)]" />
                  <span>https://nutramilo.com</span>
                </div>
                <a
                  href="https://nutramilo.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nutramilo-open-tab"
                  aria-label="Open Nutramilo in new tab"
                  className="ml-2 inline-flex items-center justify-center w-7 h-7 border border-[var(--sci-border)] hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] text-[var(--sci-text-muted)] transition-colors"
                >
                  <ExternalLink size={12} />
                </a>
              </div>

              {/* Viewport */}
              <div className="relative aspect-[16/10] bg-[var(--sci-bg)] overflow-hidden">
                {/* Skeleton shimmer behind iframe */}
                {!iframeLoaded && !iframeFailed && (
                  <div className="absolute inset-0 z-[1]" data-testid="nutramilo-skeleton">
                    <div className="absolute inset-0 bg-[var(--sci-bg)]" />
                    <div
                      className="absolute inset-0 opacity-60"
                      style={{
                        background:
                          "linear-gradient(110deg, transparent 30%, rgba(0,122,138,0.12) 50%, transparent 70%)",
                        backgroundSize: "200% 100%",
                        animation: "sci-shimmer 1.8s linear infinite",
                      }}
                    />
                    {/* Mock UI lines */}
                    <div className="absolute inset-0 p-6 md:p-10 flex flex-col gap-4">
                      <div className="h-3 w-32 bg-[var(--sci-border)]" />
                      <div className="h-8 w-3/5 bg-[var(--sci-border)]" />
                      <div className="h-4 w-4/5 bg-[var(--sci-border)] mt-2" />
                      <div className="h-4 w-2/3 bg-[var(--sci-border)]" />
                      <div className="mt-auto grid grid-cols-3 gap-2">
                        <div className="h-8 bg-[var(--sci-border)]" />
                        <div className="h-8 bg-[var(--sci-border)]" />
                        <div className="h-8 bg-[var(--sci-border)]" />
                      </div>
                    </div>
                  </div>
                )}

                {!iframeFailed && (
                  <iframe
                    src="https://nutramilo.com/"
                    title="Nutramilo live preview"
                    data-testid="nutramilo-iframe"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    onLoad={() => setIframeLoaded(true)}
                    onError={() => setIframeFailed(true)}
                    className="absolute inset-0 w-full h-full border-0 z-[2]"
                  />
                )}

                {/* Fallback overlay if iframe is blocked */}
                {iframeFailed && (
                  <div className="absolute inset-0 bg-[var(--sci-bg)] z-[3]">
                    <FallbackPreview />
                  </div>
                )}
              </div>

              {/* Footer caption */}
              <div className="px-4 py-3 border-t border-[var(--sci-border)] flex items-center justify-between text-[10px] font-mono-sci tracking-[0.2em] text-[var(--sci-text-muted)]">
                <span>NUTRAMILO · AI HEALTH PLATFORM</span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[var(--sci-teal)] sci-pulse-dot rounded-full" />
                  LIVE PREVIEW
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── PILLARS ──────────────────────────────────────────────────────── */}
        <div className="mt-16 md:mt-24">
          <div className="overline text-[var(--sci-teal)] mb-6">
            · Three intelligence layers
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.a
                  key={p.title}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nutramilo-pillar-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: 0.07 * i }}
                  className="group bg-white p-8 md:p-10 hover:bg-[var(--sci-navy)] hover:text-white transition-colors duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="font-mono-sci text-[11px] tracking-[0.25em] text-[var(--sci-text-muted)] group-hover:text-white/60">
                      {p.n} / 03
                    </div>
                    <div className="w-11 h-11 border border-[var(--sci-border)] group-hover:border-white/25 flex items-center justify-center text-[var(--sci-teal)] group-hover:text-white transition-colors">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                  </div>
                  <h4 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] group-hover:text-white tracking-tight leading-tight transition-colors">
                    {p.title}
                  </h4>
                  <p className="mt-4 text-sm md:text-[15px] text-[var(--sci-text-muted)] group-hover:text-white/75 leading-relaxed transition-colors">
                    {p.text}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium group-hover:text-[var(--sci-teal)] transition-colors">
                    {p.cta}
                    <ArrowUpRight size={14} />
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>

        {/* ── 18 DIGITAL TWINS GRID ────────────────────────────────────────── */}
        <div className="mt-16 md:mt-24">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-8">
            <div className="lg:col-span-7">
              <div className="overline text-[var(--sci-teal)] mb-4">
                · 18 Digital Twins · 37 Physiological Links
              </div>
              <h4 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[var(--sci-navy)] tracking-tight leading-[1.05]">
                Not one twin — <em className="not-italic text-[var(--sci-teal)]">an entire team</em>.
              </h4>
            </div>
            <p className="lg:col-span-5 text-[var(--sci-text-muted)] leading-relaxed">
              Each twin tracks a different body system. Together they form a
              complete clinical picture — with peer-reviewed physiological
              cross-references in real time.
            </p>
          </div>

          <div
            data-testid="nutramilo-twins-grid"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]"
          >
            {TWINS.map((t, i) => (
              <div
                key={t[0]}
                data-testid={`nutramilo-twin-${i}`}
                className="bg-white p-4 md:p-5 hover:bg-[var(--sci-bg)] transition-colors"
              >
                <div className="font-mono-sci text-[10px] tracking-[0.2em] text-[var(--sci-teal)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif-display text-base md:text-lg text-[var(--sci-navy)] leading-tight mt-1.5">
                  {t[0]}
                </div>
                <div className="text-[11px] text-[var(--sci-text-muted)] mt-1.5 leading-snug">
                  {t[1]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── DEEP LINKS + SCIENTIFIC SOURCES ──────────────────────────────── */}
        <div className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Deep links */}
          <div className="lg:col-span-7">
            <div className="overline text-[var(--sci-teal)] mb-5">
              · Deep-link directory
            </div>
            <h4 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight mb-8">
              Jump straight into the platform.
            </h4>

            <div className="grid sm:grid-cols-2 gap-3">
              {DEEP_LINKS.map((dl, i) => (
                <a
                  key={dl.href}
                  href={dl.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nutramilo-deeplink-${i}`}
                  className="group flex items-start justify-between gap-4 bg-white border border-[var(--sci-border)] p-5 hover:border-[var(--sci-teal)] transition-colors"
                >
                  <div>
                    <div className="font-serif-display text-lg text-[var(--sci-navy)] leading-tight group-hover:text-[var(--sci-teal)] transition-colors">
                      {dl.label}
                    </div>
                    <div className="text-xs text-[var(--sci-text-muted)] mt-2 leading-relaxed">
                      {dl.detail}
                    </div>
                    <div className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)] mt-2 truncate max-w-[260px]">
                      {dl.href.replace(/^https?:\/\//, "")}
                    </div>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-[var(--sci-text-muted)] group-hover:text-[var(--sci-teal)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Scientific sources */}
          <div className="lg:col-span-5">
            <div className="overline text-[var(--sci-teal)] mb-5">
              · Built on peer-reviewed evidence
            </div>
            <h4 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight mb-8">
              Scientific data sources.
            </h4>

            <div className="bg-[var(--sci-navy)] text-white p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-5">
                  <Globe size={14} className="text-[var(--sci-teal)]" />
                  <span className="overline text-white/65">
                    10+ Reference Databases
                  </span>
                </div>
                <div
                  data-testid="nutramilo-sources"
                  className="flex flex-wrap gap-2"
                >
                  {SOURCES.map((s) => (
                    <span
                      key={s}
                      className="text-[11px] font-mono-sci tracking-[0.15em] border border-white/15 px-2.5 py-1.5 text-white/85"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-sm text-white/65 leading-relaxed">
                  Each recommendation in Nutramilo is anchored in publicly
                  accessible, peer-reviewed sources — the same editorial
                  standard upheld across Sci Ink Press journals.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="https://nutramilo.com/science/papers"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nutramilo-sources-cta"
                    className="inline-flex items-center gap-2 text-sm text-white border-b border-white/40 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] pb-0.5 transition-colors"
                  >
                    Browse publications
                    <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3 text-xs text-[var(--sci-text-muted)] border border-[var(--sci-border)] bg-white p-4">
              <Stethoscope size={14} className="text-[var(--sci-teal)] shrink-0 mt-0.5" />
              <span className="leading-relaxed">
                Nutramilo is not a medical device per MDR 2017/745 and does not
                replace medical consultation.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Fallback preview (used when iframe is blocked by X-Frame-Options) ── */
function FallbackPreview() {
  return (
    <div className="absolute inset-0 p-6 md:p-10 flex flex-col">
      <div className="flex-1 flex flex-col justify-center">
        <div className="font-mono-sci text-[10px] tracking-[0.25em] text-[var(--sci-teal)] mb-4">
          NUTRAMILO · LIVE PLATFORM
        </div>
        <h5 className="font-serif-display text-3xl md:text-5xl text-[var(--sci-navy)] tracking-tight leading-[0.98]">
          Your AI Digital <br />
          <em className="not-italic text-[var(--sci-teal)]">Health Twin.</em>
        </h5>
        <p className="mt-4 text-sm md:text-base text-[var(--sci-text-muted)] max-w-md">
          Personalized clinical-nutrition guidance built on peer-reviewed
          science. 18 organ-system twins. Real-time biomarker translation.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="text-[10px] font-mono-sci tracking-[0.2em] border border-[var(--sci-border)] px-2 py-1 text-[var(--sci-navy)]">
            HbA1c · 5.7
          </span>
          <span className="text-[10px] font-mono-sci tracking-[0.2em] border border-[var(--sci-border)] px-2 py-1 text-[var(--sci-navy)]">
            VAIS · 52
          </span>
          <span className="text-[10px] font-mono-sci tracking-[0.2em] border border-[var(--sci-border)] px-2 py-1 text-[var(--sci-navy)]">
            DIL · LOW
          </span>
          <span className="text-[10px] font-mono-sci tracking-[0.2em] border border-[var(--sci-border)] px-2 py-1 text-[var(--sci-navy)]">
            STREAK · 12d
          </span>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {["Health", "Nutrition", "Precision"].map((s) => (
          <div
            key={s}
            className="bg-[var(--sci-navy)] text-white text-[10px] font-mono-sci tracking-[0.2em] px-2 py-2.5 text-center"
          >
            {s.toUpperCase()}
          </div>
        ))}
      </div>
    </div>
  );
}
